import { useState, useEffect } from 'react';

/**
 * Image component with animated skeleton loader and error fallback
 * 
 * Usage:
 * ```tsx
 * <ImgWithSkeleton
 *   src={imageUrl}
 *   alt="Description"
 *   fallbackSrc="/assets/placeholders/placeholder-winery-logo.png"
 *   className="h-12 w-auto object-contain"
 * />
 * ```
 */
interface ImgWithSkeletonProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallbackSrc?: string;
  skeletonClassName?: string;
}

export const ImgWithSkeleton = ({
  src,
  alt,
  fallbackSrc = '/assets/placeholders/placeholder-winery-logo.png',
  className = '',
  skeletonClassName = '',
  ...imgProps
}: ImgWithSkeletonProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  // Reset state when src changes and check if image is already loaded
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setCurrentSrc(src);

    // Check if image is already cached/loaded
    const img = new Image();
    img.src = src;
    if (img.complete) {
      setIsLoading(false);
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    if (!hasError && fallbackSrc && currentSrc !== fallbackSrc) {
      setHasError(true);
      setCurrentSrc(fallbackSrc);
    }
  };

  return (
    <div className="relative inline-block w-full h-full">
      {/* Animated skeleton - shown while loading */}
      {isLoading && (
        <div
          className={`absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] ${skeletonClassName}`}
          style={{
            animation: 'shimmer 1.5s ease-in-out infinite',
          }}
        />
      )}

      {/* Actual image */}
      <img
        src={currentSrc}
        alt={alt}
        className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onLoad={handleLoad}
        onError={handleError}
        {...imgProps}
      />

      {/* Keyframes for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};
